import { bytes32Schema, nonzeroAddressSchema, type Address, type Hex } from '@hoodmorning/contracts';
import {
  decodeFunctionResult,
  encodeAbiParameters,
  encodeFunctionData,
  getAddress,
  getCreate2Address,
  isAddressEqual,
  parseAbi,
  parseAbiParameters,
} from 'viem';
import { verifyContractCode } from './codeVerification.js';
import { createCanonicalBlockReadClient, type CanonicalReadPort } from './canonicalBlockReadClient.js';

export const graduationFactoryAbi = parseAbi([
  'function isLaunchCurve(address) view returns (bool)',
  'function curveForToken(address) view returns (address)',
]);
export const graduationCurveAbi = parseAbi([
  'function factory() view returns (address)',
  'function token() view returns (address)',
  'function quote() view returns (address)',
  'function lifecycle() view returns (uint8)',
  'function graduationAdapter() view returns (address)',
  'function graduationAdapterCodeHash() view returns (bytes32)',
  'function graduate(bytes data) returns ((address locker,address hook,bytes32 poolId,uint128 baseUsed,uint128 quoteUsed) r)',
]);
export const graduationAdapterAbi = parseAbi([
  'function factory() view returns (address)',
  'function manager() view returns (address)',
  'function completed(address) view returns (bool)',
  'function hookInitCodeHash(address) view returns (bytes32)',
  'function lockerAddress(address) view returns (address)',
]);

type Pin = { address: Address; codeHash: Hex };
export type GraduationRelease = {
  factory: Pin;
  adapter: Pin;
  manager: Pin;
  curveCodeHash: Hex;
};

const emptyCode = (value: Hex | undefined) => value === undefined || value === '0x';
const same = (left: unknown, right: string) => typeof left === 'string' && isAddressEqual(left as Address, right as Address);

/**
 * Prepares one permissionless graduation attempt against an exact reviewed
 * release and recent canonical source. The CREATE2 salt is mined offchain, but
 * its resulting hook address and required permission bits are independently
 * checked here. A failed transaction can be prepared again because the curve
 * and adapter make the migration atomic; this helper never authorizes a retry
 * from an unknown transaction outcome.
 */
export async function prepareGraduation(input: {
  /** Read-only RPC port; must bound request duration/bytes and honor no retries. */
  client: CanonicalReadPort;
  curve: Address;
  token: Address;
  quote: Address;
  caller: Address;
  salt: Hex;
  release: GraduationRelease;
  /** Current milliseconds; injectable only by the trusted composition/tests. */
  now?: () => number;
}) {
  const curve = getAddress(nonzeroAddressSchema.parse(input.curve));
  const token = getAddress(nonzeroAddressSchema.parse(input.token));
  const quote = getAddress(nonzeroAddressSchema.parse(input.quote));
  const caller = getAddress(nonzeroAddressSchema.parse(input.caller));
  const salt = bytes32Schema.parse(input.salt) as Hex;
  const release = structuredClone(input.release);
  for (const pin of [release.factory, release.adapter, release.manager]) {
    pin.address = getAddress(nonzeroAddressSchema.parse(pin.address));
    pin.codeHash = bytes32Schema.parse(pin.codeHash) as Hex;
  }
  release.curveCodeHash = bytes32Schema.parse(release.curveCodeHash) as Hex;

  const snapshot = await createCanonicalBlockReadClient({
    port: input.client,
    // Initial/final chain and header, six code reads, thirteen relationships,
    // and one exact graduate simulation. No failover or numbered-state fallback.
    policy: { maxRequests: 24, maxAgeSeconds: 120, maxFutureSeconds: 30 },
    ...(input.now === undefined ? {} : { now: input.now }),
  });
  const client = snapshot.client;
  const block = snapshot.source;
  const blockNumber = block.number;
  const code = await verifyContractCode(
    { getBytecode: args => client.getBytecode({ address: args.address, blockNumber }) },
    [
      { name: 'graduation-factory', address: release.factory.address, expectedCodeHash: release.factory.codeHash },
      { name: 'graduation-adapter', address: release.adapter.address, expectedCodeHash: release.adapter.codeHash },
      { name: 'graduation-manager', address: release.manager.address, expectedCodeHash: release.manager.codeHash },
      { name: 'graduation-curve', address: curve, expectedCodeHash: release.curveCodeHash },
    ],
  );
  if (!code.valid) throw new Error('Graduation release code verification failed');

  const [registered, registeredCurve, curveFactory, curveToken, curveQuote, lifecycle, adapter, adapterHash,
    adapterFactory, manager, completed, hookInitCodeHash, locker] = await Promise.all([
    client.readContract({ address: release.factory.address, abi: graduationFactoryAbi, functionName: 'isLaunchCurve', args: [curve], blockNumber }),
    client.readContract({ address: release.factory.address, abi: graduationFactoryAbi, functionName: 'curveForToken', args: [token], blockNumber }),
    ...(['factory', 'token', 'quote', 'lifecycle', 'graduationAdapter', 'graduationAdapterCodeHash'] as const)
      .map(functionName => client.readContract({ address: curve, abi: graduationCurveAbi, functionName, blockNumber })),
    client.readContract({ address: release.adapter.address, abi: graduationAdapterAbi, functionName: 'factory', blockNumber }),
    client.readContract({ address: release.adapter.address, abi: graduationAdapterAbi, functionName: 'manager', blockNumber }),
    client.readContract({ address: release.adapter.address, abi: graduationAdapterAbi, functionName: 'completed', args: [curve], blockNumber }),
    client.readContract({ address: release.adapter.address, abi: graduationAdapterAbi, functionName: 'hookInitCodeHash', args: [curve], blockNumber }),
    client.readContract({ address: release.adapter.address, abi: graduationAdapterAbi, functionName: 'lockerAddress', args: [curve], blockNumber }),
  ]);
  if (registered !== true || !same(registeredCurve, curve) || !same(curveFactory, release.factory.address)
    || !same(curveToken, token) || !same(curveQuote, quote) || lifecycle !== 1
    || !same(adapter, release.adapter.address) || typeof adapterHash !== 'string'
    || adapterHash.toLowerCase() !== release.adapter.codeHash.toLowerCase()
    || !same(adapterFactory, release.factory.address) || !same(manager, release.manager.address)
    || completed !== false || typeof hookInitCodeHash !== 'string' || typeof locker !== 'string') {
    throw new Error('Graduation source relationships mismatch');
  }
  const initCodeHash = bytes32Schema.parse(hookInitCodeHash) as Hex;
  const permanentLocker = getAddress(nonzeroAddressSchema.parse(locker));
  const predictedHook = getCreate2Address({ from: release.adapter.address, salt, bytecodeHash: initCodeHash });
  if ((BigInt(predictedHook) & 0x3fffn) !== 0x20ccn) throw new Error('Graduation hook salt has invalid permission bits');
  const [hookCode, lockerCode] = await Promise.all([
    client.getBytecode({ address: predictedHook, blockNumber }),
    client.getBytecode({ address: permanentLocker, blockNumber }),
  ]);
  if (!emptyCode(hookCode) || !emptyCode(lockerCode)) throw new Error('Graduation CREATE2 destination already occupied');

  const adapterData = encodeAbiParameters(parseAbiParameters('bytes32'), [salt]);
  const data = encodeFunctionData({ abi: graduationCurveAbi, functionName: 'graduate', args: [adapterData] });
  const simulation = await client.call({ account: caller, to: curve, data, blockNumber });
  if (!simulation.data) throw new Error('Graduation simulation returned no result');
  const result = decodeFunctionResult({ abi: graduationCurveAbi, functionName: 'graduate', data: simulation.data });
  if (!same(result.locker, permanentLocker) || !same(result.hook, predictedHook)
    || result.poolId === `0x${'0'.repeat(64)}` || result.baseUsed <= 0n || result.quoteUsed <= 0n) {
    throw new Error('Graduation simulation result mismatch');
  }
  await snapshot.verifySource();
  return {
    call: { to: curve, value: 0n, data },
    caller,
    salt,
    predictedHook,
    permanentLocker,
    poolId: result.poolId,
    baseUsed: result.baseUsed,
    quoteUsed: result.quoteUsed,
    sourceBlockNumber: blockNumber,
    sourceBlockHash: block.hash,
    sourceBlockTimestamp: block.timestamp,
    simulation: 'passed-at-source-block' as const,
    retryPolicy: 'reconcile-unknown-outcome-before-new-attempt' as const,
  };
}
